import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { apiGet } from "./api";

export interface Parish {
  // Only populated by PublicParishProvider (via /api/parish/public/:slug).
  // AuthedParishProvider's /api/parish/settings predates this field and
  // doesn't need it — the scheduler surfaces never link a parish id
  // client-side. Used by the volunteer signup flow to tag a new
  // account with its parish (see VolunteerSignup.tsx).
  id?: string;
  slug: string;
  name?: string;
  display_name: string;
  logo_url: string | null;
  accent_color: string;
  updated_at?: string;
  // Capability slug for the public schedule page. Only present on the
  // AUTHENTICATED settings payload (AuthedParishProvider) — the public
  // /api/parish/public/:slug branding endpoint deliberately omits it.
  public_schedule_slug?: string;
  // Whether MSP is effectively connected (per-parish token or the
  // legacy worker secret). Authenticated settings payload only.
  // undefined (older worker / public payload) must read as "don't
  // nag" — only an explicit false shows the connect-MSP banner.
  msp_connected?: boolean;
  // Billing (authenticated settings payload only). status is the
  // EFFECTIVE status — the worker already maps an expired trial to
  // past_due. undefined = older worker payload; show no billing UI.
  status?: string;
  trial_ends_at?: string | null;
  has_billing?: boolean;
}

// Default parish used when no slug is known, before the API call
// resolves, or when running in stub mode against an empty Supabase.
// Matches the spec's neutral-blue default accent.
const DEFAULT_PARISH: Parish = {
  slug: "default",
  display_name: "Parish",
  logo_url: null,
  accent_color: "#3B6FB0",
};

interface ParishState {
  parish: Parish;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const ParishContext = createContext<ParishState | null>(null);

export function useParish(): ParishState {
  const ctx = useContext(ParishContext);
  if (!ctx) {
    // Soft default — components reading useParish outside any provider
    // (rare, mostly during boot) get the fallback rather than crashing.
    return {
      parish: DEFAULT_PARISH,
      loading: false,
      error: null,
      refresh: async () => {},
    };
  }
  return ctx;
}

// Authenticated parish provider — fetches /api/parish/settings.
// Used inside SessionProvider on the dashboard / settings surfaces.
export function AuthedParishProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ParishState>(() => ({
    parish: DEFAULT_PARISH,
    loading: true,
    error: null,
    refresh: async () => {},
  }));

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await apiGet<{ parish: Parish }>("/parish/settings");
      const parish = normalize(res.parish);
      setState({ parish, loading: false, error: null, refresh: load });
    } catch (err) {
      // Soft-fail to default parish — chrome still renders. Theme
      // is applied by the route-scoped <SacristyTheme>/<ParishTheme>
      // boundary, not here.
      setState({
        parish: DEFAULT_PARISH,
        loading: false,
        error: (err as Error).message,
        refresh: load,
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return <ParishContext.Provider value={{ ...state, refresh: load }}>{children}</ParishContext.Provider>;
}

// Public parish provider — fetches /api/parish/public/:slug.
// Used on the unauthenticated help center route where there is no
// session to derive a parish from; the slug comes from the URL.
export function PublicParishProvider({
  slug,
  fallbackDisplayName,
  children,
}: {
  slug: string;
  fallbackDisplayName?: string;
  children: ReactNode;
}) {
  const [state, setState] = useState<ParishState>(() => ({
    parish: { ...DEFAULT_PARISH, slug, display_name: fallbackDisplayName ?? DEFAULT_PARISH.display_name },
    loading: true,
    error: null,
    refresh: async () => {},
  }));

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await apiGet<{ parish: Parish }>(`/parish/public/${encodeURIComponent(slug)}`);
      const parish = normalize(res.parish);
      setState({ parish, loading: false, error: null, refresh: load });
    } catch (err) {
      // Soft-fail to fallback (slug + frontmatter name) on error.
      // Theme is applied by the route-scoped <ParishTheme> boundary,
      // not here.
      const fallback: Parish = {
        ...DEFAULT_PARISH,
        slug,
        display_name: fallbackDisplayName ?? DEFAULT_PARISH.display_name,
      };
      setState({
        parish: fallback,
        loading: false,
        error: (err as Error).message,
        refresh: load,
      });
    }
  }, [slug, fallbackDisplayName]);

  useEffect(() => {
    void load();
  }, [load]);

  return <ParishContext.Provider value={{ ...state, refresh: load }}>{children}</ParishContext.Provider>;
}

function normalize(p: Parish): Parish {
  return {
    ...p,
    display_name: p.display_name?.trim() || p.name?.trim() || DEFAULT_PARISH.display_name,
    accent_color: p.accent_color || DEFAULT_PARISH.accent_color,
  };
}
